import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getFormattedDate, convertToNumberKWD } from "../../utils/common";
import {
  calculateVariantItemPrice,
  calculateChargeableItems,
  calculateItemDisplayPrice,
  calculateTotalChargeableItemsCost,
  calculateTotalPackageBasePrice,
  calculatePackagePrice,
  calculateItemDisplayPriceInPackage,
  getSelectedOptions
} from "../../utils/catering";
import { validatePackageCompletion } from "../../utils/paxCalculations";
import ShareQuotationIcon from "../../assets/icons/ShareQuotationIcon";
import ShareQuotationModal from "./ShareQuotationModal";
import { useSelector, useDispatch } from "react-redux";

import { selectCateringCartPackages, selectCateringCartItems, selectCateringCartSubtotal, selectCateringCartTotal, selectCateringCartFilters, selectCateringRestaurantId, selectCateringBrandsList, selectIsPastOrderRestoration, selectPendingPayment, selectCateringCartNotes, selectCateringAreaCharges, selectCateringAdditionalServiceFilters, selectCateringDeliveryCharge, selectCateringDiscountPrice, selectIsReorderMode, selectReorderOrderId, selectReorderOriginalTotal } from "../../redux/features/restaurants/restaurantSelectors";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import { selectCateringSearchedCustomer, selectCateringSelectedAddress } from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import { selectSearchedCompany, selectCompanySelectedAddress } from "../../redux/features/company/companySelectors";
import { getCateringAreaCharges } from "../../api/get/getCateringAreaCharges";
import { addToCateringCart } from "../../api/post/addToCateringCart";
import { addToCombinedCateringCart } from "../../api/post/addToCombinedCateringCart";
import { deleteCombinedCateringCart } from "../../api/post/deleteCombinedCateringCart";
import { processManualOrder } from "../../api/post/processManualOrder";
import { processCateringManualOrder } from "../../api/post/processCateringManualOrder";
import { processCombinedCateringManualOrder } from "../../api/post/processCombinedCateringManualOrder";
import { getCateringOrderStatus } from "../../api/get/getCateringOrderStatus";
import { resendCateringPaymentLink } from "../../api/post/resendCateringPaymentLink";
import { transformCateringCartPayload, transformCombinedCateringCartPayload, transformReorderPayload, transformCombinedReorderPayload } from "../../utils/catering";
import { emptyCateringCartAsync } from "../../redux/features/restaurants/restaurantThunks";
import { clearCateringCart, setCateringCartFilters, setIsPastOrderRestoration, setPendingPayment, clearPendingPayment, setCateringCartNotes, setCateringAreaCharges, clearReorderMode } from "../../redux/features/restaurants/restaurantSlice";
import { reorderCateringPaidCancelOrder } from "../../api/post/reorderCateringPaidCancelOrder";
import { reorderCombinedCateringPaidCancelOrder } from "../../api/post/reorderCombinedCateringPaidCancelOrder";

const CateringOrderSummaryReadonly = ({ selectedPackages, showPaymentOptions = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formattedDate = getFormattedDate();
  const filters = useSelector(selectCateringCartFilters);
  const additionalServiceFilters = useSelector(selectCateringAdditionalServiceFilters);

  const [orderedPackages, setOrderedPackages] = useState([]);
  const [showPendingPayment, setShowPendingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null); // Store order_no and reference_number for polling
  const [isResendingPayment, setIsResendingPayment] = useState(false);
  const [resendPaymentError, setResendPaymentError] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [isBelbayit, setIsBelbayit] = useState(false);
  const [belbayitAmount, setBelbayitAmount] = useState("");
  const [belbayitOrderId, setBelbayitOrderId] = useState("");
  const [isCombinedOrder, setIsCombinedOrder] = useState(false); // Track if this is a combined order
  
  // Get area charges from Redux
  const areaChargesFromRedux = useSelector(selectCateringAreaCharges);
  const deliveryChargeFromRedux = useSelector(selectCateringDeliveryCharge);
  const discountPrice = useSelector(selectCateringDiscountPrice); // Get from Redux, read-only
  
  // Get pending payment state from Redux
  const pendingPaymentFromRedux = useSelector(selectPendingPayment);
  const brandList = useSelector(selectCateringBrandsList);
  const cateringRestaurantId = useSelector(selectCateringRestaurantId);
  const cateringRestaurantName = brandList?.find(brand => brand.id == cateringRestaurantId)?.name_en;
  const serviceType = useSelector(selectServiceType);
  
  // Get notes from Redux (retained from CateringOrderSummary)
  const notes = useSelector(selectCateringCartNotes);
  
  // Check if this is a past order restoration (should hide Book Slot button) - from Redux
  const isPastOrderRestoration = useSelector(selectIsPastOrderRestoration);
  
  // Get reorder mode state from Redux
  const isReorderMode = useSelector(selectIsReorderMode);
  const reorderOrderId = useSelector(selectReorderOrderId);
  const reorderOriginalTotal = useSelector(selectReorderOriginalTotal);
  
  // Individual catering selectors
  const cateringCustomerInfo = useSelector(selectCateringSearchedCustomer);
  const cateringSelectedAddress = useSelector(selectCateringSelectedAddress);
  
  // Corporate catering selectors
  const companyInfo = useSelector(selectSearchedCompany);
  const companySelectedAddress = useSelector(selectCompanySelectedAddress);

  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const cateringCartItems = useSelector(selectCateringCartItems);
  const cateringCartSubtotal = useSelector(selectCateringCartSubtotal);
  const cateringCartTotal = useSelector(selectCateringCartTotal);

  // Don't clean up the flag on component unmount since we navigate between pages
  // Only clean it up after successful order processing or explicit user action
  
  // Restore pending payment state when component mounts
  useEffect(() => {
    if (pendingPaymentFromRedux) {
      console.log('Restoring pending payment state from Redux:', pendingPaymentFromRedux);
      setShowPendingPayment(true);
      setPaymentStatus(pendingPaymentFromRedux.status);
      setOrderInfo(pendingPaymentFromRedux.orderInfo);
      
      // If payment is still pending, resume polling
      if (pendingPaymentFromRedux.status === "Pending" && pendingPaymentFromRedux.orderInfo?.order_no) {
        startPaymentStatusPolling(
          pendingPaymentFromRedux.orderInfo.order_no,
          pendingPaymentFromRedux.orderInfo.reference_number
        );
      }
    }
  }, []);

  // Validate if all required package items have been added based on pax_limit
  const packageValidation = useMemo(() => {
    if (!orderedPackages || orderedPackages.length === 0) {
      return {
        isValid: false,
        missingCategories: [],
        message: 'No packages in cart'
      };
    }

    // For multiple packages, all must be valid
    const validationResults = orderedPackages.map(pkg =>
      validatePackageCompletion(pkg, filters.pax)
    );

    const allValid = validationResults.every(result => result.isValid);
    const allMissingCategories = validationResults.flatMap(result =>
      result.missingCategories || []
    );

    return {
      isValid: allValid,
      missingCategories: allMissingCategories,
      message: allValid
        ? 'All packages are complete'
        : `Missing items in ${allMissingCategories.length} categories across packages`
    };
  }, [orderedPackages, filters.pax]);

  // Check if there are items in the catering cart AND all required items are added
  const hasItemsInCart = cateringCartPackages.length > 0 || (orderedPackages && orderedPackages.length > 0);

  useEffect(() => {
    // Always use cateringCartPackages instead of selectedPackages prop
    if (cateringCartPackages && cateringCartPackages.length > 0) {
      setOrderedPackages(cateringCartPackages.map(pkg => ({
        ...pkg,
        items: Array.isArray(pkg.items) ? pkg.items.map(item => ({ ...item })) : []
      })));
    } else {
      setOrderedPackages([]);
    }
  }, [cateringCartPackages]);

  // Calculate total chargeable items cost across all packages - use individual PAX for each package
  const totalChargeableItemsCost = useMemo(() => {
    return orderedPackages.reduce((total, pkg) => {
      const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
      return total + calculateTotalChargeableItemsCost([pkg], paxToUse);
    }, 0);
  }, [orderedPackages, filters.pax]);

  // Calculate total package base prices - use individual PAX for each package
  const totalPackageBasePrice = useMemo(() => {
    return orderedPackages.reduce((total, pkg) => {
      const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
      return total + calculateTotalPackageBasePrice([pkg], paxToUse);
    }, 0);
  }, [orderedPackages, filters.pax]);

  // Calculate subtotal (before delivery and discount)
  const subtotal = useMemo(() => {
    return totalPackageBasePrice + totalChargeableItemsCost;
  }, [totalPackageBasePrice, totalChargeableItemsCost]);

  // Calculate final total payable amount (including delivery charge, excluding discount)
  const totalPayable = useMemo(() => {
    return Math.max(0, subtotal + deliveryCharge - discountPrice);
  }, [subtotal, deliveryCharge, discountPrice]);

  // Fetch area charges on component mount (only if not in Redux)
  useEffect(() => {
    const fetchAreaCharges = async () => {
      // Only fetch if not already in Redux (check for undefined or null, not just empty)
      if (areaChargesFromRedux !== undefined && areaChargesFromRedux !== null) {
        console.log("Area charges already in Redux, skipping API call");
        return;
      }

      try {
        console.log("Fetching area charges from API...");
        const response = await getCateringAreaCharges("3");
        console.log("API Response:", response);
        if (response && response.data) {
          dispatch(setCateringAreaCharges(response.data));
          console.log("Area charges loaded and saved to Redux:", response.data);
        } else {
          console.error("No data in API response:", response);
        }
      } catch (error) {
        console.error("Error fetching area charges:", error);
      }
    };

    fetchAreaCharges();
  }, [areaChargesFromRedux, dispatch]);

  // Initialize delivery charge from Redux first (priority to edited value)
  useEffect(() => {
    if (deliveryChargeFromRedux !== undefined && deliveryChargeFromRedux !== null) {
      console.log("💰 [READONLY] Using delivery charge from Redux:", deliveryChargeFromRedux);
      setDeliveryCharge(deliveryChargeFromRedux);
    }
  }, [deliveryChargeFromRedux]);

  // Calculate delivery charge based on selected address area ID (fallback if not in Redux)
  useEffect(() => {
    // Skip if delivery charge is already set from Redux
    if (deliveryChargeFromRedux > 0) {
      console.log("💰 [READONLY] Skipping auto-calculation, using Redux value:", deliveryChargeFromRedux);
      return;
    }

    console.log("🔍 [READONLY] DELIVERY CHARGE CALCULATION DEBUG:");
    console.log("🔍 Service Type:", serviceType);
    console.log("🔍 Full cateringSelectedAddress:", cateringSelectedAddress);
    console.log("🔍 Full companySelectedAddress:", companySelectedAddress);
    console.log("🔍 Area charges from Redux:", areaChargesFromRedux);
    
    let selectedAreaId = null;
    let selectedAreaName = null;
    
    if (serviceType === "individual catering" && cateringSelectedAddress) {
      // For individual catering, get area ID and name from selected address
      selectedAreaId = cateringSelectedAddress.area_id;
      selectedAreaName = cateringSelectedAddress.area;
      console.log("🔍 Individual catering - extracted area_id:", selectedAreaId);
    } else if (serviceType === "corporate catering" && companySelectedAddress) {
      // For corporate catering, get area ID and name from company address
      selectedAreaId = companySelectedAddress.area_id;
      selectedAreaName = companySelectedAddress.area;
      console.log("🔍 Corporate catering - extracted area_id:", selectedAreaId);
    } else {
      console.log("🔍 No address selected or service type not matched");
    }

    console.log("💰 Selected area ID:", selectedAreaId);
    console.log("💰 Selected area name:", selectedAreaName);

    if (areaChargesFromRedux && areaChargesFromRedux.length > 0 && selectedAreaId) {
      console.log("💰 Attempting to match area ID:", selectedAreaId);
      console.log("💰 Available area charges:", areaChargesFromRedux.map(a => ({ id: a.id, name: a.name_en, charge: a.delivery_charge })));
      
      // Match by area ID
      const matchingArea = areaChargesFromRedux.find(
        area => area.id === selectedAreaId || area.id === parseInt(selectedAreaId)
      );
      
      console.log("💰 Matched area:", matchingArea);

      if (matchingArea && matchingArea.delivery_charge) {
        const charge = parseFloat(matchingArea.delivery_charge.replace(/[^\d.]/g, '')) || 0;
        setDeliveryCharge(charge);
        console.log("✅ 💰 [READONLY] Delivery charge set to:", charge, "KWD");
      } else {
        setDeliveryCharge(0);
        console.log("❌ 💰 [READONLY] No matching area found or no delivery charge");
      }
    } else {
      setDeliveryCharge(0);
      console.log("❌ [READONLY] Missing data:", {
        hasAreaCharges: !!areaChargesFromRedux,
        areaChargesLength: areaChargesFromRedux?.length || 0,
        hasSelectedAreaId: !!selectedAreaId
      });
    }
  }, [serviceType, cateringSelectedAddress, companySelectedAddress, areaChargesFromRedux, deliveryChargeFromRedux]);

  const handleBack = () => {
    // Clear the past order restoration flag when user goes back (handled by Redux)
    dispatch(setIsPastOrderRestoration(false));
    navigate(-1);
  };

  const handleShareQuotation = () => {
    console.log("Share Quotation clicked for catering packages:", orderedPackages);
    console.log("Notes:", notes);
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleResendPaymentLink = async () => {
    try {
      setIsResendingPayment(true);
      setResendPaymentError(null);

      // Get the order ID from orderInfo (which should be set after successful order processing)
      if (!orderInfo || !orderInfo.order_id) {
        throw new Error('Order information not available. Cannot resend payment link.');
      }

      console.log('Resending payment link for order:', {
        orderId: orderInfo.order_id,
        isCombinedOrder: isCombinedOrder
      });

      // Call the resend payment link API with order type
      const response = await resendCateringPaymentLink(orderInfo.order_id, isCombinedOrder);

      if (response && response.data) {
        console.log('Payment link resent successfully:', response.data);
        alert('Payment link has been resent successfully!');
      } else {
        throw new Error('Failed to resend payment link. Please try again.');
      }
    } catch (error) {
      console.error('Error resending payment link:', error);
      setResendPaymentError(error.message || 'Failed to resend payment link. Please try again.');
    } finally {
      setIsResendingPayment(false);
    }
  };

  const handleCateringOrder = async (payLater = false) => {
    try {
      setIsLoading(true);
      setError(null);

      // Determine customer and address based on service type
      let customerId, addressId, companyId;
      
      if (serviceType === "individual catering") {
        // For individual catering, use catering customer data
        if (!cateringCustomerInfo?.id) {
          throw new Error('Customer information is required for individual catering');
        }
        if (!cateringSelectedAddress?.id) {
          throw new Error('Address selection is required for individual catering');
        }
        customerId = cateringCustomerInfo.id;
        companyId = 0;
        addressId = cateringSelectedAddress.id;
      } else if (serviceType === "corporate catering") {
        // For corporate catering, use company data
        if (!companyInfo?.id) {
          throw new Error('Company information is required for corporate catering');
        }
        if (!companySelectedAddress?.id) {
          throw new Error('Company address selection is required for corporate catering');
        }
        customerId = 0;
        companyId = companyInfo.id;
        addressId = companySelectedAddress.id;
      } else {
        throw new Error('Invalid service type. Must be "individual catering" or "corporate catering"');
      }

      if (!orderedPackages || orderedPackages.length === 0) {
        throw new Error('No packages to process');
      }

      if (!cateringRestaurantId) {
        throw new Error('Restaurant information is required');
      }

      // Determine if we have multiple packages (main + additional services)
      const hasMultiplePackages = orderedPackages.length > 1;
      
      // Set the combined order flag for resend payment link
      setIsCombinedOrder(hasMultiplePackages);

      if (hasMultiplePackages) {
        // Use combined cart API for multiple packages
        console.log('Multiple packages detected, using combined cart API');

        // Step 1: Delete combined cart (if exists)
        // Note: combine_cart_id is typically 1 or can be fetched if stored
        const deleteCombinedPayload = {
          agent_id: "1",
          country_id: "3",
          combine_cart_id: "1",
          customer_id: customerId.toString(),
          company_id: companyId.toString()
        };

        console.log('Deleting existing combined catering cart:', deleteCombinedPayload);
        try {
          await deleteCombinedCateringCart(deleteCombinedPayload);
        } catch (error) {
          console.log('No existing combined cart to delete or error deleting:', error);
          // Continue even if delete fails (cart might not exist)
        }

        // Step 2: Add to combined catering cart
        const combinedCartPayload = transformCombinedCateringCartPayload({
          customerId,
          companyId,
          addressId,
          packages: orderedPackages,
          mainFilters: filters,
          additionalServiceFilters,
          notes,
          deliveryCharge,
          countryId: 3
        });

        console.log('Adding to combined catering cart:', combinedCartPayload);
        const cartResponse = await addToCombinedCateringCart(combinedCartPayload);

        if (!cartResponse || !cartResponse.data) {
          throw new Error('Failed to add items to combined cart');
        }

        console.log('Combined cart response:', cartResponse);
      } else {
        // Use regular cart API for single package
        console.log('Single package detected, using regular cart API');

        // Step 1: Empty existing catering cart
        const deleteCartPayload = {
          agent_id: "1",
          country_id: "3",
          restaurant_id: cateringRestaurantId.toString(),
          catering_id: orderedPackages[0].id.toString(),
          customer_id: customerId.toString(),
          company_id: companyId.toString()
        };

        console.log('Deleting existing catering cart:', deleteCartPayload);
        await dispatch(emptyCateringCartAsync(deleteCartPayload));

        // Step 2: Add to catering cart
        const addToCartPayload = transformCateringCartPayload({
          customerId,
          companyId,
          addressId,
          packages: orderedPackages,
          filters,
          notes,
          restaurantId: cateringRestaurantId,
          branchId: 0,
          countryId: 3
        });

        console.log('Adding to catering cart:', addToCartPayload);
        const cartResponse = await addToCateringCart(addToCartPayload);

        if (!cartResponse || !cartResponse.data || !cartResponse.data.service_item_cart_id) {
          throw new Error('Failed to add items to cart');
        }

        console.log('Cart response:', cartResponse);
      }

      // Step 3: Process catering manual order
      // Use different API based on whether it's a combined order (multiple packages)
      let orderResponse;
      
      if (hasMultiplePackages) {
        // Use combined catering manual order API
        // Calculate bilbayt discount if bilbayt is enabled
        const bilbaytDiscount = isBelbayit && belbayitAmount 
          ? (totalPayable - parseFloat(belbayitAmount)).toFixed(3)
          : "0.000";
        
        const processOrderPayload = {
          country_id: "3",
          customer_id: customerId.toString(),
          company_id: companyId.toString(),
          address_id: addressId,
          payment_method_id: "1",
          delivery_charge: deliveryCharge.toFixed(3),
          order_sub_total_price: subtotal.toFixed(3),
          discount_price: discountPrice.toFixed(3),
          order_total_price: totalPayable.toFixed(3),
          currency: "KWD",
          is_discount_applied: discountPrice > 0 ? "1" : "",
          promo_code: "",
          comments: notes || "",
          pay_later: payLater,
          is_bilbayt: isBelbayit,
          ...(isBelbayit && {
            bilbayt_discount: bilbaytDiscount,
            bilbayt_price: parseFloat(belbayitAmount).toFixed(3),
            bilbayt_order_id: belbayitOrderId.trim()
          })
        };

        console.log('Processing combined catering manual order:', processOrderPayload);
        orderResponse = await processCombinedCateringManualOrder(processOrderPayload);
      } else {
        // Use regular catering manual order API
        // Calculate bilbayt discount if bilbayt is enabled
        const bilbaytDiscount = isBelbayit && belbayitAmount 
          ? (totalPayable - parseFloat(belbayitAmount)).toFixed(3)
          : "0.000";
        
        const processOrderPayload = {
          country_id: "3",
          restaurant_id: cateringRestaurantId,
          catering_id: orderedPackages[0].id.toString(),
          customer_id: customerId.toString(),
          company_id: companyId.toString(),
          address_id: addressId,
          payment_method_id: "1",
          order_sub_total_price: subtotal.toFixed(3),
          order_total_price: totalPayable.toFixed(3),
          delivery_charge: deliveryCharge.toFixed(3),
          discount_price: discountPrice.toFixed(3),
          currency: "KWD",
          is_discount_applied: discountPrice > 0 ? "1" : "",
          promo_code: "",
          comments: notes || "",
          pay_later: payLater,
          is_bilbayt: isBelbayit,
          ...(isBelbayit && {
            bilbayt_discount: bilbaytDiscount,
            bilbayt_price: parseFloat(belbayitAmount).toFixed(3),
            bilbayt_order_id: belbayitOrderId.trim()
          })
        };

        console.log('Processing single catering manual order:', processOrderPayload);
        orderResponse = await processCateringManualOrder(processOrderPayload);
      }
      
      // Handle different response structures
      // Combined API function returns response.data directly (already unwrapped once)
      // Regular API function also returns response.data (already unwrapped once)
      // But their internal structures are different:
      // - Combined: response.data contains { status, data: { order_id, order_no, ... } }
      // - Regular: response.data contains { data: { data: { order_id, order_no, ... } } }
      let orderData;
      if (hasMultiplePackages) {
        // Combined catering manual order response structure
        // API returns: { status: "ok", data: { order_id, order_no, ... } }
        if (!orderResponse || !orderResponse.data) {
          console.error('Combined order response:', orderResponse);
          throw new Error('Failed to process combined order');
        }
        orderData = orderResponse.data; // Direct access to data
        console.log('Combined order data extracted:', orderData);
      } else {
        // Regular catering manual order response structure
        // API returns: { data: { data: { order_id, order_no, ... } } }
        if (!orderResponse || !orderResponse.data || !orderResponse.data.data) {
          console.error('Regular order response:', orderResponse);
          throw new Error('Failed to process order');
        }
        orderData = orderResponse.data.data; // Nested data.data
        console.log('Regular order data extracted:', orderData);
      }

      // Store order info for polling
      // Note: Combined order API may not return reference_number
      setOrderInfo({
        order_id: orderData.order_id,
        order_no: orderData.order_no,
        reference_number: orderData.reference_number || ""
      });

      // Success - show pending payment or completion based on pay_later
      setShowPendingPayment(true);
      setPaymentStatus(payLater ? "Booked" : "Pending");
      console.log('Catering order processed successfully:', orderResponse);
      
      // Save pending payment state to Redux (only for Pay Now)
      if (!payLater) {
        dispatch(setPendingPayment({
          status: "Pending",
          orderInfo: orderData,
          timestamp: new Date().toISOString()
        }));
      }
      
      // If pay_later is false (Pay Now), start polling for payment status
      if (!payLater && orderData.order_no) {
        startPaymentStatusPolling(orderData.order_no, orderData.reference_number);
      } else if (payLater) {
        // For Book Slot, payment is completed immediately, so handle success
        handlePaymentSuccess();
      }

    } catch (error) {
      console.error('Error processing catering order:', error);
      setError(error.message || 'Failed to process order');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookSlot = () => handleCateringOrder(true);
  const handlePayNow = () => {
    // Check if in reorder mode
    if (isReorderMode) {
      handleReorder();
    } else {
      handleCateringOrder(false);
    }
  };

  // Handle reorder for paid cancelled orders
  const handleReorder = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!reorderOrderId) {
        throw new Error('Reorder order ID not found');
      }

      if (!orderedPackages || orderedPackages.length === 0) {
        throw new Error('No packages to reorder');
      }

      // Check if order total has changed
      const epsilon = 0.001;
      const totalDifference = Math.abs(totalPayable - reorderOriginalTotal);
      const hasChanged = totalDifference > epsilon;

      if (!hasChanged) {
        // No changes - button should be disabled, but handle gracefully
        alert('No changes detected. Order total remains the same.');
        setIsLoading(false);
        return;
      }

      // Determine if upgrade or downgrade
      const isUpgrade = totalPayable > reorderOriginalTotal;
      const reorderType = isUpgrade ? "upgrade" : "downgrade";

      // Check if this is a combined order (multiple packages)
      const isCombinedOrder = orderedPackages.length > 1;

      console.log('🔄 Processing reorder:', {
        orderId: reorderOrderId,
        isCombinedOrder,
        packagesCount: orderedPackages.length,
        originalTotal: reorderOriginalTotal,
        currentTotal: totalPayable,
        difference: totalPayable - reorderOriginalTotal,
        type: reorderType
      });

      let reorderPayload;
      let response;

      if (isCombinedOrder) {
        // Combined order reorder
        console.log('🎯 Processing COMBINED reorder');
        
        reorderPayload = transformCombinedReorderPayload({
          packages: orderedPackages,
          filters,
          deliveryCharge,
          discountPrice,
          originalTotal: reorderOriginalTotal,
          currentTotal: totalPayable,
          combineOrderId: reorderOrderId,
          comments: notes
        });

        console.log('📦 Combined reorder payload:', reorderPayload);

        // Call combined reorder API
        response = await reorderCombinedCateringPaidCancelOrder(reorderOrderId, reorderPayload);
      } else {
        // Single order reorder
        console.log('📦 Processing SINGLE reorder');
        
        reorderPayload = transformReorderPayload({
          packages: orderedPackages,
          filters,
          deliveryCharge,
          discountPrice,
          originalTotal: reorderOriginalTotal,
          currentTotal: totalPayable,
          comments: notes
        });

        console.log('📦 Single reorder payload:', reorderPayload);

        // Call single reorder API
        response = await reorderCateringPaidCancelOrder(reorderOrderId, reorderPayload);
      }

      console.log('✅ Reorder response:', response);

      if (isUpgrade) {
        // For upgrades, show pending payment and start polling
        const orderData = response.data || response;
        
        setOrderInfo({
          order_id: orderData.order_id || reorderOrderId,
          order_no: orderData.order_no || "",
          reference_number: orderData.reference_number || ""
        });

        setShowPendingPayment(true);
        setPaymentStatus("Pending");

        // Save pending payment state to Redux
        dispatch(setPendingPayment({
          status: "Pending",
          orderInfo: orderData,
          timestamp: new Date().toISOString()
        }));

        // Start polling for payment status
        if (orderData.order_no) {
          startPaymentStatusPolling(orderData.order_no, orderData.reference_number);
        }
      } else {
        // For downgrades, order is complete - show success and navigate
        alert('Order successfully modified! Amount will be refunded.');
        
        // Clear reorder mode
        dispatch(clearReorderMode());
        
        // Clear cart and navigate
        dispatch(clearCateringCart());
        dispatch(setCateringCartFilters({
          pax: 20,
          date: "",
          time: ""
        }));
        
        navigate("/catering-order");
      }

    } catch (error) {
      console.error('❌ Error processing reorder:', error);
      setError(error.message || 'Failed to process reorder');
    } finally {
      setIsLoading(false);
    }
  };

  // Start polling for payment status
  const startPaymentStatusPolling = (orderNo, referenceNumber = "") => {
    console.log('Starting payment status polling for order:', { orderNo, referenceNumber });
    
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Poll immediately first, then every 5 seconds
    const pollStatus = async () => {
      try {
        console.log('Polling payment status...');
        const statusResponse = await getCateringOrderStatus(orderNo, referenceNumber);
        console.log('Payment status poll response:', statusResponse);

        // The API returns the data directly, not nested in a data property
        if (statusResponse) {
          const { catering_order_status } = statusResponse;
          console.log('Current catering order status:', catering_order_status);
          
          // Update payment status based on catering_order_status
          if (catering_order_status === "Submitted" || catering_order_status === "Paid" || catering_order_status === "Accepted") {
            // Payment successful
            console.log('Payment successful, stopping polling');
            setPaymentStatus("Submitted");
            
            // Update Redux pending payment status
            dispatch(setPendingPayment({
              status: "Submitted",
              orderInfo,
              timestamp: new Date().toISOString()
            }));
            
            stopPaymentStatusPolling();
            handlePaymentSuccess();
          } else if (catering_order_status === "Failed" || catering_order_status === "Canceled") {
            // Payment failed
            console.log('Payment failed, stopping polling');
            setPaymentStatus("Failed");
            
            // Update Redux pending payment status
            dispatch(setPendingPayment({
              status: "Failed",
              orderInfo,
              timestamp: new Date().toISOString()
            }));
            
            stopPaymentStatusPolling();
          } else if (catering_order_status === "Pending Payment") {
            // Still pending, keep polling
            console.log('Payment still pending, continuing to poll...');
            setPaymentStatus("Pending");
            
            // Update Redux pending payment with latest timestamp
            dispatch(setPendingPayment({
              status: "Pending",
              orderInfo,
              timestamp: new Date().toISOString()
            }));
          }
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
        // Don't stop polling on error, might be temporary network issue
      }
    };

    // Poll immediately
    pollStatus();
    
    // Then poll every 5 seconds
    pollingIntervalRef.current = setInterval(pollStatus, 5000);
  };

  // Stop polling for payment status
  const stopPaymentStatusPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Handle successful payment - reset cart and navigate
  const handlePaymentSuccess = () => {
    // Stop polling
    stopPaymentStatusPolling();
    
    // Clear pending payment state from Redux
    dispatch(clearPendingPayment());
    
    // Wait a moment to show success message, then reset and navigate
    setTimeout(() => {
      // Clear catering cart (this also clears isPastOrderRestoration and pastOrderAddressId in Redux)
      dispatch(clearCateringCart());
      
      // Reset filters
      dispatch(setCateringCartFilters({
        pax: 20,
        date: "",
        time: ""
      }));
      
      // Navigate to catering order page
      navigate("/catering-order");
    }, 2000); // 2 second delay to show success message
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPaymentStatusPolling();
    };
  }, []);

  // Payment status label logic similar to orderSummary
  const paymentStatusLabel = useMemo(() => {
    const statuses = [
      { status: "Pending", label: "Pending Payment" },
      { status: "Failed", label: "Payment Failed" },
      { status: "Submitted", label: "Payment Successful" },
      { status: "Booked", label: "Slot Booked" },
    ];
    return (
      statuses.find((s) => s.status === paymentStatus)?.label || paymentStatus
    );
  }, [paymentStatus]);

  return (
    <div className="w-full h-full bg-white flex flex-col items-center">
      <div className="w-full max-w-[500px] overflow-y-auto p-4 pb-40 flex flex-col">
        <div className="border border-gray-200 w-full p-4 rounded-md mt-5 flex flex-col flex-grow">
          <div className="flex justify-between items-center mb-1">
            <h2 className="text-lg font-bold text-[#959FA8]">
              {cateringRestaurantName}
            </h2>
            <p className="text-sm text-[#959FA8]">{formattedDate}</p>
          </div>
          {orderedPackages && orderedPackages.length > 0 ? orderedPackages.map((pkg, index) => (
            <React.Fragment key={pkg.id}>
              <div className="flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-bold text-[#959FA8]">
                    {pkg?.name_en || pkg?.title}
                  </h2>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <p className="text-lg font-bold text-[#959FA8]">
                    {(() => {
                      // Check if this is a standalone package (Additional Setup)
                      const packageName = pkg.name_en || pkg.title || '';
                      const isStandaloneSetup = packageName.toLowerCase().includes('additional setup');
                      const hasStandalone = isStandaloneSetup && (
                        pkg.items?.some(item => item.price_type === "Standalone Item Based") ||
                        pkg.categories?.some(cat => cat.catering_service_item?.some(item => item.price_type === "Standalone Item Based"))
                      );
                      
                      // For standalone packages, don't show PAX
                      if (hasStandalone) {
                        return "Standalone Items";
                      }
                      
                      const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
                      return `${paxToUse} PAX`;
                    })()}
                  </p>
                  <p className="text-lg font-bold text-[#959FA8]">
                    KWD {(() => {
                      const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
                      return calculatePackagePrice(pkg, paxToUse).toFixed(3);
                    })()}
                  </p>
                </div>

                {/* Display items for this specific package - READONLY */}
                {pkg.items && pkg.items.length > 0 && (
                  <div className="ml-4 mb-4">
                    {pkg.items.map((item) => (
                      <div key={item.id} className="mb-2">
                        {item.has_variants ? (
                          // Render variant items - READONLY
                          item.items?.map((variantItem, variantIndex) => (
                            <div
                              key={variantItem.variantId}
                              className="text-sm text-[#959FA8] font-semibold"
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                  <span className="mr-2 text-xs">
                                    {variantItem.quantity}x
                                  </span>
                                  <span>{item.name || item.name_en}</span>
                                </div>
                                <span>
                                  KWD{" "}
                                  {(() => {
                                    const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
                                    const totalDisplayPrice = calculateItemDisplayPriceInPackage(item, variantItem, pkg, paxToUse);
                                    return totalDisplayPrice.toFixed(3);
                                  })()}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 ml-[30px]">
                                {getSelectedOptions(variantItem.selectedVariant)}
                              </div>
                              {variantItem.instructions && (
                                <div
                                  className="text-xs text-gray-400 ml-[30px] italic truncate"
                                  title={variantItem.instructions}
                                >
                                  Note: {variantItem.instructions}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          // Render non-variant items - READONLY
                          <div className="text-sm text-[#959FA8] font-semibold flex justify-between items-center">
                            <div className="flex items-center">
                              <span className="mr-2 text-xs">
                                {item.quantity}x
                              </span>
                              <span>
                                {item.name_en || item.name}
                                {item.add_on_type && (
                                  <span className="ml-1 text-xs text-gray-500 capitalize">
                                    ({item.add_on_type})
                                  </span>
                                )}
                                {item.complimentaryCount > 0 && (
                                  <span className="ml-1 text-xs text-green-600 font-semibold">
                                    [{item.complimentaryCount} comp.]
                                  </span>
                                )}
                              </span>
                            </div>
                            <span>
                              KWD{" "}
                              {(() => {
                                const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
                                const totalDisplayPrice = calculateItemDisplayPriceInPackage(item, null, pkg, paxToUse);
                                return totalDisplayPrice.toFixed(3);
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          )) : (
            <div className="text-center text-gray-500 py-8">
              No packages selected
            </div>
          )}

          {/* Setup Note Section */}
          {orderedPackages && orderedPackages.length > 0 && orderedPackages[0] && (
            <>
              <div className="mt-4">
                <img
                  src="/Images/Menu/hr.png"
                  className="w-full mb-3"
                  alt="separator"
                />
                <h2 className="text-lg font-bold text-[#959FA8] mb-3">
                  Setup Note
                </h2>
                <div className="space-y-2">
                  {/* Service Duration */}
                  {orderedPackages[0].service_duration && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Service Duration:</span> {orderedPackages[0].service_duration}
                      </div>
                    </div>
                  )}
                  
                  {/* Setup Time */}
                  {orderedPackages[0].setup_time && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Setup Time:</span> {orderedPackages[0].setup_time}
                      </div>
                    </div>
                  )}
                  
                  {/* Electric Outlet */}
                  {orderedPackages[0].electric_outlet && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Electric Outlet:</span> Required
                      </div>
                    </div>
                  )}
                  
                  {/* Water Supply */}
                  {orderedPackages[0].water_supply && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Water Supply:</span> {orderedPackages[0].water_supply_specs || 'Required'}
                      </div>
                    </div>
                  )}
                  
                  {/* Special Notes */}
                  {orderedPackages[0].special_notes_en && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Special Notes:</span> {orderedPackages[0].special_notes_en}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="mt-4">
            <img
              src="/Images/Menu/hr.png"
              className="w-full mb-4"
              alt="separator"
            />
            
            {/* Total Payment Section */}
            <div className="border border-gray-300 rounded-lg p-4 mb-4">
              <div className="mb-3">
                <h2 className="text-lg font-bold text-[#959FA8]">Total Payment</h2>
              </div>
              
              <div className="space-y-3">
                {/* Per-Package Breakdown */}
                {orderedPackages && orderedPackages.length > 0 && orderedPackages.map((pkg, index) => {
                  const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
                  const packageBasePrice = calculateTotalPackageBasePrice([pkg], paxToUse);
                  const packageChargeableItems = calculateTotalChargeableItemsCost([pkg], paxToUse);
                  const packageSubtotal = packageBasePrice + packageChargeableItems;
                  
                  return (
                    <div key={pkg.id} className="space-y-1">
                      {/* Package Header */}
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-[#959FA8]">
                          {pkg.name_en || pkg.title} {pkg.isAdditionalService && '(Additional Service)'}
                        </span>
                      </div>
                      
                      {/* Package Service Items */}
                      <div className="flex justify-between pl-3">
                        <span className="text-xs text-[#959FA8]">Service Items ({paxToUse} PAX)</span>
                        <span className="text-xs text-[#959FA8]">KWD {packageBasePrice.toFixed(3)}</span>
                      </div>
                      
                      {/* Package Add Ons */}
                      {packageChargeableItems > 0 && (
                        <div className="flex justify-between pl-3">
                          <span className="text-xs text-[#959FA8]">Add Ons</span>
                          <span className="text-xs text-[#959FA8]">KWD {packageChargeableItems.toFixed(3)}</span>
                        </div>
                      )}
                      
                      {/* Package Subtotal */}
                      <div className="flex justify-between pl-3 pt-1 border-t border-gray-200">
                        <span className="text-sm font-semibold text-[#959FA8]">Subtotal</span>
                        <span className="text-sm font-semibold text-[#959FA8]">KWD {packageSubtotal.toFixed(3)}</span>
                      </div>
                      
                      {/* Separator between packages */}
                      {index < orderedPackages.length - 1 && (
                        <div className="border-t border-gray-300 my-2"></div>
                      )}
                    </div>
                  );
                })}
                
                {/* Overall Summary Section */}
                {orderedPackages && orderedPackages.length > 0 && (
                  <>
                    <div className="border-t-2 border-gray-400 my-3"></div>
                    
                    {/* Total Service Items */}
                    <div className="flex justify-between">
                      <span className="text-sm text-[#959FA8]">Total Service Items</span>
                      <span className="text-sm text-[#959FA8]">KWD {totalPackageBasePrice.toFixed(3)}</span>
                    </div>
                    
                    {/* Total Add Ons */}
                    <div className="flex justify-between">
                      <span className="text-sm text-[#959FA8]">Total Add Ons</span>
                      <span className="text-sm text-[#959FA8]">KWD {totalChargeableItemsCost.toFixed(3)}</span>
                    </div>
                    
                    {/* Delivery Charges */}
                    <div className="flex justify-between">
                      <span className="text-sm text-[#959FA8]">Delivery Charges</span>
                      <span className="text-sm text-[#959FA8]">KWD {deliveryCharge.toFixed(3)}</span>
                    </div>
                    
                    {/* VAT */}
                    <div className="flex justify-between">
                      <span className="text-sm text-[#959FA8]">VAT</span>
                      <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                    </div>
                    
                    <div className="border-t border-gray-300 my-2"></div>
                    
                    {/* Subtotal (before discount) */}
                    <div className="flex justify-between">
                      <span className="text-sm text-[#959FA8]">Subtotal</span>
                      <span className="text-sm text-[#959FA8]">KWD {(subtotal + deliveryCharge).toFixed(3)}</span>
                    </div>
                    
                    {/* Discount - Read-only (set in CateringOrderSummary) */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#959FA8]">Discount</span>
                      <span className="text-sm text-[#959FA8]">KWD {discountPrice.toFixed(3)}</span>
                    </div>
                    
                    <div className="border-t-2 border-gray-400 my-2"></div>
                    
                    {/* Total Amount */}
                    <div className="flex justify-between">
                      <span className="text-base font-bold text-[#959FA8]">Total Amount</span>
                      <span className="text-base font-bold text-[#959FA8]">KWD {totalPayable.toFixed(3)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-2 w-full">
            <textarea
              className="border border-gray-300 w-full h-[100px] p-3 rounded-md text-sm font-semibold text-[#959FA8]"
              placeholder="Notes;"
              value={notes}
              onChange={(e) => dispatch(setCateringCartNotes(e.target.value))}
            ></textarea>
          </div>

          {/* Bilbayt Checkbox */}
          {showPaymentOptions && !showPendingPayment && (
            <div className="mt-4">
              <div className="flex items-center p-3 border border-gray-300 rounded-md transition-colors">
                <input
                  type="checkbox"
                  id="belbayit-checkbox"
                  checked={isBelbayit}
                  onChange={(e) => {
                    setIsBelbayit(e.target.checked);
                    if (!e.target.checked) {
                      setBelbayitAmount(""); // Clear amount when unchecked
                      setBelbayitOrderId(""); // Clear order ID when unchecked
                    } else {
                      // Pre-fill with total payable amount when checked
                      setBelbayitAmount(totalPayable.toFixed(3));
                    }
                  }}
                  className="w-4 h-4 text-[#86604D] bg-gray-100 border-gray-300 rounded cursor-pointer"
                />
                <label
                  htmlFor="belbayit-checkbox"
                  className="ml-3 flex items-center text-sm font-semibold text-[#959FA8] cursor-pointer"
                >
                  <img 
                    src="/Images/bilbayt.jpeg" 
                    alt="Bilbayt" 
                    className="w-6 h-6 mr-2 object-contain"
                  />
                  Paid via Bilbayt
                </label>
              </div>
              
              {/* Bilbayt Amount Input - Show when checkbox is checked */}
              {isBelbayit && (
                <div className="mt-3 p-3 border border-amber-200 bg-amber-50 rounded-md">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Bilbayt Payment Amount (KWD) <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-600">KWD</span>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={belbayitAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string or valid number input
                        if (value === '' || !isNaN(parseFloat(value))) {
                          setBelbayitAmount(value);
                        }
                      }}
                      placeholder="Enter amount paid via Bilbayt"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Total Order Amount: KWD {totalPayable.toFixed(3)}
                  </p>
                  {belbayitAmount && parseFloat(belbayitAmount) > 0 && (
                    <p className="text-xs text-amber-700 mt-1 font-semibold">
                      ✓ Bilbayt Amount: KWD {parseFloat(belbayitAmount).toFixed(3)}
                    </p>
                  )}
                  {belbayitAmount && parseFloat(belbayitAmount) > 0 && totalPayable > parseFloat(belbayitAmount) && (
                    <p className="text-xs text-blue-700 mt-1 font-semibold">
                      Discount: KWD {(totalPayable - parseFloat(belbayitAmount)).toFixed(3)}
                    </p>
                  )}
                  
                  {/* Bilbayt Order ID Input */}
                  <div className="mt-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Bilbayt Order ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={belbayitOrderId}
                      onChange={(e) => setBelbayitOrderId(e.target.value)}
                      placeholder="Enter Bilbayt order ID (e.g., BILBAYT20251106)"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                      required
                    />
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Payment status display - similar to orderSummary */}
          {showPendingPayment && (
            <div className="flex flex-col justify-center items-center mt-8 mb-4">
              <button
                className={`w-full sm:w-[222px] h-10 text-lg font-semibold p-2 rounded ${
                  paymentStatusLabel === "Pending Payment"
                    ? "bg-[#F7B501] text-white" // Styles for Pending Payment
                    : paymentStatusLabel === "Payment Failed"
                    ? "bg-[#F70101] text-white" // Styles for Payment Failed
                    : paymentStatusLabel === "Payment Successful"
                    ? "bg-[#00AF0D] text-white" // Styles for Payment Successful
                    : paymentStatusLabel === "Slot Booked"
                    ? "bg-[#00AF0D] text-white" // Styles for Slot Booked
                    : ""
                }`}
              >
                {paymentStatusLabel}
              </button>

              {/* Additional text below status */}
              {paymentStatusLabel === "Pending Payment" && (
                <div className="text-center mt-2 px-4 max-w-xs">
                  <p className="text-sm text-gray-700">
                    Payment link has been sent to the customer. Waiting payment
                    to be completed.
                  </p>
                </div>
              )}
              {paymentStatusLabel === "Slot Booked" && (
                <div className="text-center mt-2 px-4 max-w-xs">
                  <p className="text-sm text-gray-700">
                    Catering slot has been successfully booked. Payment can be completed later.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error message display */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-red-800">
                    Error Processing Order
                  </h4>
                  <p className="text-xs text-red-700 mt-1">
                    {error}
                  </p>
                  <button 
                    onClick={() => setError(null)}
                    className="text-xs text-red-600 hover:text-red-800 mt-1 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Resend Payment Link Error Display */}
          {resendPaymentError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-red-800">
                    Error Resending Payment Link
                  </h4>
                  <p className="text-xs text-red-700 mt-1">
                    {resendPaymentError}
                  </p>
                  <button 
                    onClick={() => setResendPaymentError(null)}
                    className="text-xs text-red-600 hover:text-red-800 mt-1 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="w-full bg-white pt-4">
            <div className="flex flex-col items-center">
              {showPaymentOptions ? (
                // Catering Checkout Page Buttons
                <div className="flex flex-col gap-3 w-full max-w-[412px]">
                  {/* Share Quotation Button */}
                  {/* <button
                    onClick={handleShareQuotation}
                    className="w-full h-10 text-lg font-semibold bg-[#13305D] text-white p-2 rounded flex items-center justify-center"
                  >
                    Share Quotation <ShareQuotationIcon className="ml-2 w-4 h-4" />
                  </button> */}
                  
                  <div className="flex flex-row gap-3">
                    <button
                      onClick={handleBack}
                      className="w-[120px] h-10 text-[#86604D] border border-[#86604D] p-2 text-sm font-semibold rounded hover:bg-[#86604D] hover:text-white transition-colors"
                    >
                      Back
                    </button>
                    {showPendingPayment ? (
                      <button
                        onClick={handleResendPaymentLink}
                        disabled={isResendingPayment}
                        className={`flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors ${
                          isResendingPayment ? "opacity-50" : ""
                        }`}
                      >
                        <div className="flex items-center justify-around">
                          <span className="text-sm px-2">
                            {isResendingPayment ? "Resending..." : "Resend Payment Link"}
                          </span>
                          {!isResendingPayment && (
                            <img
                              src="/Images/Menu/resend.svg"
                              alt="resend"
                              className="ml-2"
                            />
                          )}
                        </div>
                      </button>
                    ) : (
                      <div className="flex flex-col gap-2 flex-1">
                        {/* Reorder mode - only show Pay Now button */}
                        {isReorderMode ? (
                          <>
                            {/* Check if order total has changed */}
                            {(() => {
                              const epsilon = 0.001;
                              const totalDifference = Math.abs(totalPayable - reorderOriginalTotal);
                              const hasChanged = totalDifference > epsilon;
                              const isUpgrade = totalPayable > reorderOriginalTotal;
                              const isDowngrade = totalPayable < reorderOriginalTotal;
                              
                              return (
                                <>
                                  {/* Show message about upgrade/downgrade */}
                                  {hasChanged && (
                                    <div className={`text-xs p-2 rounded ${
                                      isUpgrade ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-green-50 text-green-800 border border-green-200'
                                    }`}>
                                      {isUpgrade ? (
                                        <>
                                          <div className="font-semibold">⬆️ Upgrade Detected</div>
                                          <div>Additional payment required: KWD {(totalPayable - reorderOriginalTotal).toFixed(3)}</div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="font-semibold">⬇️ Downgrade Detected</div>
                                          <div>Refund amount: KWD {(reorderOriginalTotal - totalPayable).toFixed(3)}</div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                  
                                  <button
                                    onClick={handlePayNow}
                                    disabled={isLoading || !hasChanged}
                                    className={`h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors ${
                                      isLoading || !hasChanged ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                                    title={!hasChanged ? "No changes detected - order total is the same" : "Confirm reorder"}
                                  >
                                    {isLoading ? "Processing..." : hasChanged ? (isUpgrade ? "Pay Now (Upgrade)" : "Confirm (Downgrade)") : "No Changes Detected"}
                                  </button>
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <>
                            {/* Normal mode - show Book Slot and Pay Now buttons */}
                            <button
                              onClick={handleBookSlot}
                              disabled={isLoading || (isBelbayit && (!belbayitAmount || parseFloat(belbayitAmount) <= 0 || !belbayitOrderId.trim()))}
                              className={`h-10 text-sm font-semibold bg-[#13305D] text-white p-2 rounded hover:bg-[#0F2749] transition-colors ${
                                isLoading || (isBelbayit && (!belbayitAmount || parseFloat(belbayitAmount) <= 0 || !belbayitOrderId.trim())) ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                              title={isBelbayit && (!belbayitAmount || parseFloat(belbayitAmount) <= 0 || !belbayitOrderId.trim()) ? "Please enter Bilbayt payment amount and order ID" : ""}
                            >
                              {isLoading ? "Processing..." : "Reserve"}
                            </button>
                            {!isBelbayit && (
                              <button
                                onClick={handlePayNow}
                                disabled={isLoading}
                                className={`h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors ${
                                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                              >
                                {isLoading ? "Processing..." : "Pay Now"}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {/* {showShareModal && <ShareQuotationModal onClose={handleCloseShareModal} />} */}
    </div>
  );
};

export default CateringOrderSummaryReadonly;
